import React, { useEffect, useState } from "react";
import { RotateCcw, Loader2, Plus, X, CheckCircle2 } from "lucide-react";
import api from "../../services/api";
import { useSettings } from "../../context/SettingsContext";

const REASONS = ["Produit défectueux", "Ne correspond pas", "Erreur de référence", "Non compatible avec mon véhicule", "Autre"];

const ReturnsSection = ({ preselectOrder, clearPreselect }) => {
  const { return_days } = useSettings();
  const [returns, setReturns] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [selected, setSelected] = useState({});
  const [reason, setReason] = useState(REASONS[0]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    Promise.all([api.get("/returns"), api.get("/orders")])
      .then(([r, o]) => { setReturns(r.data); setOrders(o.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (preselectOrder) {
      setShowForm(true);
      setOrderId(preselectOrder.id);
      setSelected({});
      clearPreselect && clearPreselect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectOrder]);

  const currentOrder = orders.find((o) => o.id === orderId);

  const toggle = (pid) => setSelected((s) => ({ ...s, [pid]: !s[pid] }));

  const submit = async () => {
    setError("");
    const items = Object.keys(selected).filter((k) => selected[k]).map((pid) => {
      const it = currentOrder.items.find((i) => i.id === pid);
      return { product_id: pid, qty: it.qty, reason };
    });
    if (!orderId || items.length === 0) { setError("Sélectionnez une commande et au moins un article."); return; }
    setSubmitting(true);
    try {
      await api.post("/returns", { order_id: orderId, items, reason });
      setShowForm(false); setSelected({}); setOrderId("");
      setLoading(true); load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur lors de la demande.");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#FF3B3B]" size={32} /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-extrabold italic uppercase tracking-tight text-slate-50 text-2xl md:text-3xl flex items-center gap-2"><RotateCcw size={26} className="text-[#FF3B3B]" /> Mes retours</h1>
        {!showForm && orders.length > 0 && (
          <button onClick={() => setShowForm(true)} className="btn-orange rounded-lg px-4 py-2.5 font-bold flex items-center gap-2"><Plus size={18} /> Nouvelle demande</button>
        )}
      </div>

      {showForm && (
        <div className="bg-[#10131A] rounded-2xl border border-[#1E232D] p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-slate-50 text-lg">Nouvelle demande de retour</h2>
            <button onClick={() => { setShowForm(false); setError(""); }} className="text-slate-500 hover:text-white"><X size={20} /></button>
          </div>
          <label className="block text-[14px] font-semibold text-slate-50 mb-1.5">Commande concernée</label>
          <select value={orderId} onChange={(e) => { setOrderId(e.target.value); setSelected({}); }} className="w-full border-2 border-[#222735] rounded-lg px-3 py-2.5 mb-4 outline-none focus:border-[#E01F1F] text-slate-50">
            <option value="">Choisir une commande</option>
            {orders.map((o) => (<option key={o.id} value={o.id}>#{o.id.slice(0, 8).toUpperCase()} — {o.total.toFixed(2).replace(".", ",")} €</option>))}
          </select>

          {currentOrder && (
            <>
              <label className="block text-[14px] font-semibold text-slate-50 mb-2">Articles à retourner</label>
              <div className="space-y-2 mb-4">
                {currentOrder.items.map((it) => (
                  <label key={it.id} className="flex items-center gap-3 border-2 border-[#1E232D] rounded-lg p-2.5 cursor-pointer hover:border-[#E01F1F]/40">
                    <input type="checkbox" checked={!!selected[it.id]} onChange={() => toggle(it.id)} className="w-4 h-4 accent-[#E01F1F]" />
                    <img src={it.image} alt={it.name} className="w-10 h-10 rounded object-cover bg-[#08090C]" />
                    <span className="text-[14px] text-slate-50 flex-1">{it.name} <span className="text-slate-400">×{it.qty}</span></span>
                  </label>
                ))}
              </div>
              <label className="block text-[14px] font-semibold text-slate-50 mb-1.5">Motif du retour</label>
              <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full border-2 border-[#222735] rounded-lg px-3 py-2.5 mb-4 outline-none focus:border-[#E01F1F] text-slate-50">
                {REASONS.map((r) => (<option key={r} value={r}>{r}</option>))}
              </select>
            </>
          )}
          {error && <div className="text-[14px] text-red-500 mb-3">{error}</div>}
          <button onClick={submit} disabled={submitting} className="btn-orange rounded-lg px-6 py-3 font-bold flex items-center justify-center gap-2 disabled:opacity-70">
            {submitting && <Loader2 className="animate-spin" size={18} />} Envoyer la demande
          </button>
        </div>
      )}

      {returns.length === 0 && !showForm ? (
        <div className="bg-[#10131A] rounded-2xl border border-[#1E232D] p-12 text-center text-slate-400">
          <RotateCcw size={40} className="text-slate-500 mx-auto mb-3" />
          Aucune demande de retour. Vous disposez de {return_days} jours pour retourner un article.
        </div>
      ) : (
        <div className="space-y-4">
          {returns.map((r) => (
            <div key={r.id} className="bg-[#10131A] rounded-2xl border border-[#1E232D] p-5">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#1E232D] pb-3 mb-3">
                <div>
                  <span className="text-[13px] text-slate-400">Retour</span>
                  <div className="font-bold text-slate-50">#{r.id.slice(0, 8).toUpperCase()} · Cmd #{r.order_ref}</div>
                </div>
                <span className="bg-amber-950/60 text-amber-300 text-[13px] font-semibold px-3 py-1 rounded-full flex items-center gap-1.5"><CheckCircle2 size={14} /> {r.status}</span>
              </div>
              <div className="text-[13px] text-slate-400 mb-2">Motif : <span className="text-slate-50 font-medium">{r.reason}</span></div>
              <div className="space-y-1">
                {r.items.map((it, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <img src={it.image} alt={it.name} className="w-10 h-10 rounded object-cover bg-[#08090C]" />
                    <span className="text-[14px] text-slate-50">{it.name} <span className="text-slate-400">×{it.qty}</span></span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReturnsSection;
